import { createDataBackup, inspectDataBackup, RESTORE_CONFIRMATION } from '../modules/import-export-kit/index.js';
import { normalizeScenario } from './scenario.js';
import { normalizeRehearsalRecord } from './rehearsalHistory.js';

export const BACKUP_APP_ID = 'rehearsal-director';
export const BACKUP_DATA_VERSION = '1';
// Full authored scripts contain thousands of normalized cue fields. Keep the
// shared byte/depth/media/sensitive-field protections and use its public
// application-specific field budget; never alter the Warehouse component.
export const QLINE_BACKUP_POLICY = Object.freeze({ maxObjectKeys: 250000 });
export { RESTORE_CONFIRMATION };

function portableScenario(source) {
  const scenario = structuredClone(source);
  for (const character of scenario.characters || []) {
    character.privateCharacterBackground = character.secret || '';
    delete character.secret;
  }
  return JSON.parse(JSON.stringify(scenario));
}

function restoredScenario(source) {
  const scenario = structuredClone(source);
  for (const character of scenario.characters || []) {
    character.secret = character.privateCharacterBackground || '';
    delete character.privateCharacterBackground;
  }
  return normalizeScenario(scenario);
}

export function validateAppBackupPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') errors.push('Backup contents are missing.');
  if (!['full', 'scenario'].includes(payload?.kind)) errors.push('Backup type is not supported.');
  if (!Array.isArray(payload?.scenarios) || payload.scenarios.length > 500) errors.push('Scenario list is missing or too large.');
  if (payload?.kind === 'full' && (!Array.isArray(payload.history) || payload.history.length > 1000)) errors.push('Rehearsal history is missing or too large.');
  if (payload?.kind === 'full' && (!Array.isArray(payload.practiceProgress || []) || (payload.practiceProgress || []).length > 500)) errors.push('Solo Practice progress is malformed.');
  try { (payload?.scenarios || []).forEach(restoredScenario); } catch { errors.push('One or more scenarios are malformed.'); }
  try { (payload?.history || []).forEach((record) => normalizeRehearsalRecord({ ...record, scenarioSnapshot: restoredScenario(record.scenarioSnapshot) })); } catch { errors.push('One or more rehearsal records are malformed.'); }
  return { valid: errors.length === 0, errors };
}

export function createAppBackup({ scenarios = [], history = [], settings = {}, favorites = [], recentScenarios = {}, practiceProgress = [] } = {}, { createdAt = Date.now(), kind = 'full' } = {}) {
  const payload = {
    kind,
    scenarios: scenarios.map(portableScenario),
    history: kind === 'full' ? history.map((record) => ({ ...structuredClone(record), scenarioSnapshot: portableScenario(record.scenarioSnapshot) })) : [],
    settings: kind === 'full' ? structuredClone(settings || {}) : {},
    favorites: kind === 'full' ? [...new Set((favorites || []).map(String))].slice(0, 500) : [],
    recentScenarios: kind === 'full' ? structuredClone(recentScenarios || {}) : {},
    practiceProgress: kind === 'full' ? structuredClone(practiceProgress || []).slice(0, 500) : [],
  };
  return createDataBackup({ appId: BACKUP_APP_ID, dataVersion: BACKUP_DATA_VERSION, payload, createdAt }, QLINE_BACKUP_POLICY);
}

export function inspectAppBackup(text) {
  const inspected = inspectDataBackup(text, { expectedAppId: BACKUP_APP_ID, validatePayload: validateAppBackupPayload }, QLINE_BACKUP_POLICY);
  return {
    ...inspected,
    payload: {
      ...inspected.payload,
      scenarios: inspected.payload.scenarios.map(restoredScenario),
      history: (inspected.payload.history || []).map((record) => normalizeRehearsalRecord({ ...record, scenarioSnapshot: restoredScenario(record.scenarioSnapshot) })),
    },
  };
}

export function mergeById(current, incoming) {
  const merged = new Map((current || []).map((item) => [item.id, item]));
  for (const item of incoming || []) merged.set(item.id, item);
  return [...merged.values()];
}
